import api from "@/services/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// ============= TYPES =============

export type CommentType = "INTERNAL" | "PUBLIC" | "CITIZEN_RESPONSE";

export interface CommentAttachment {
  id?: string;
  key: string;
  fileName: string;
  mimeType: string;
  size: number;
}

export interface Comment {
  id: string;
  occurrenceId: string;
  authorId: string;
  type: CommentType;
  content: string;
  createdAt: string;
  updatedAt: string;
  isEdited: boolean;
  deletedAt: string | null;
  author: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    role: string;
  };
  attachments: CommentAttachment[];
  mentions: string[];
}

interface GetCommentsParams {
  occurrenceId: string;
  type?: CommentType;
  search?: string;
  page?: number;
  limit?: number;
}

interface GetCommentsResponse {
  data: Comment[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface CreateCommentData {
  type: CommentType;
  content: string;
  attachments?: Omit<CommentAttachment, "id">[];
}

interface UpdateCommentData {
  content: string;
}

// ============= QUERIES =============

export const useGetComments = (params: GetCommentsParams) => {
  return useQuery({
    queryKey: ["comments", params.occurrenceId, params.type, params.search, params.page, params.limit],
    queryFn: async () => {
      const { occurrenceId, ...queryParams } = params;
      const { data } = await api.get<GetCommentsResponse>(
        `/occurrences/${occurrenceId}/comments`,
        { params: queryParams }
      );
      return data;
    },
    enabled: !!params.occurrenceId,
  });
};

export const useGetCommentById = (occurrenceId: string, commentId: string) => {
  return useQuery({
    queryKey: ["comment", occurrenceId, commentId],
    queryFn: async () => {
      const { data } = await api.get<Comment>(
        `/occurrences/${occurrenceId}/comments/${commentId}`
      );
      return data;
    },
    enabled: !!occurrenceId && !!commentId,
  });
};

// ============= MUTATIONS =============

export const useCreateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      occurrenceId,
      data: commentData,
    }: {
      occurrenceId: string;
      data: CreateCommentData;
    }) => {
      const response = await api.post(
        `/occurrences/${occurrenceId}/comments`,
        commentData
      );
      return response.data;
    },
    onSuccess: (_, { occurrenceId }) => {
      queryClient.invalidateQueries({ queryKey: ["comments", occurrenceId] });
      queryClient.invalidateQueries({ queryKey: ["getOcurrence", occurrenceId] });
    },
  });
};

export const useUpdateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      occurrenceId,
      commentId,
      data,
    }: {
      occurrenceId: string;
      commentId: string;
      data: UpdateCommentData;
    }) => {
      const response = await api.patch(
        `/occurrences/${occurrenceId}/comments/${commentId}`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { occurrenceId, commentId }) => {
      queryClient.invalidateQueries({ queryKey: ["comments", occurrenceId] });
      queryClient.invalidateQueries({ queryKey: ["comment", occurrenceId, commentId] });
    },
  });
};

export const useDeleteComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      occurrenceId,
      commentId,
    }: {
      occurrenceId: string;
      commentId: string;
    }) => {
      const response = await api.delete(
        `/occurrences/${occurrenceId}/comments/${commentId}`
      );
      return response.data;
    },
    onSuccess: (_, { occurrenceId }) => {
      queryClient.invalidateQueries({ queryKey: ["comments", occurrenceId] });
    },
  });
};
